// src/features/sheets/constants.ts
// Centralized constants for the sheets feature
// Single source of truth for tickers, metrics, and inference patterns

// ─────────────────────────────────────────────────────────────────────────────
// Ticker Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Regex pattern for detecting valid ticker symbols (2-10 uppercase letters)
 * Used as fallback when token catalog is not available
 */
export const TICKER_SYMBOL_PATTERN = /^[A-Z]{2,10}$/;

/**
 * Check if a string looks like a valid ticker symbol
 * Uses the token catalog when provided, otherwise falls back to pattern matching
 *
 * @param value - The string to check
 * @param knownTickers - Optional set of known tickers from token catalog
 */
export function isLikelyTicker(value: string, knownTickers?: Set<string>): boolean {
  const upper = value.trim().toUpperCase();
  // If we have a known tickers set, check against it first
  if (knownTickers?.has(upper)) {
    return true;
  }
  // Fallback to pattern matching (2-10 uppercase letters)
  return TICKER_SYMBOL_PATTERN.test(upper);
}

// ─────────────────────────────────────────────────────────────────────────────
// Header Detection Patterns
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Patterns for detecting ticker/asset columns by header name
 */
export const TICKER_HEADER_PATTERNS = /^(ticker|symbol|asset|token|name|chain|protocol|coin)$/i;

/**
 * Patterns for detecting summary rows
 */
export const SUMMARY_ROW_PATTERNS = /^(average|total|sum|mean|median|min|max|avg)$/i;

// ─────────────────────────────────────────────────────────────────────────────
// Column Semantic Type Inference
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rules for inferring column semantic types from headers
 * Order matters - first match wins
 */
export const HEADER_TYPE_RULES: Array<{
  pattern: RegExp;
  type: string;
  excludePattern?: RegExp;
}> = [
  { pattern: /ticker|symbol|asset|token/i, type: "ticker" },
  { pattern: /price|value|cost/i, type: "price", excludePattern: /change/i },
  { pattern: /change|delta|%|percent|growth/i, type: "percentage" },
  { pattern: /ratio|multiple|p\/e|mc\/tvl|mc\/fee/i, type: "ratio" },
  { pattern: /date|time|day|week|month/i, type: "date" },
  { pattern: /name|description|notes|protocol/i, type: "text" },
  { pattern: /tvl|volume|mcap|market.?cap|fees|revenue/i, type: "currency" },
  { pattern: /count|users|dau|transactions/i, type: "count" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Cell Reference Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert 0-indexed column number to Excel-style letter (0=A, 25=Z, 26=AA)
 */
export function colIndexToLetter(index: number): string {
  let result = "";
  let n = index + 1;
  while (n > 0) {
    n--;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

/**
 * Convert Excel-style column letter to 0-indexed number (A=0, Z=25, AA=26)
 */
export function colLetterToIndex(col: string): number {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 64);
  }
  return index - 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// Format Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default format settings for different metric types
 */
export const METRIC_FORMATS = {
  price: { format: "currency" as const, decimals: 2 },
  percentage: { format: "percent" as const, decimals: 2 },
  currency: { format: "compact" as const, decimals: 2 },
  ratio: { format: "number" as const, decimals: 2 },
  count: { format: "compact" as const, decimals: 0 },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sheet Defaults
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_SHEET_ROWS = 50;
export const DEFAULT_SHEET_COLS = 26;
export const DEFAULT_COLUMN_WIDTH = 100;
export const MAX_PREVIEW_ROWS = 20;
export const LIVE_DATA_REFRESH_INTERVAL = 60 * 1000; // 1 minute
export const LIVE_DATA_CACHE_TTL = 60 * 1000; // 1 minute
